/**
 * Race a promise against a wall-clock timeout.
 *
 * Built for the silent-hang failure mode: a network call (Supabase Auth
 * refresh, RLS-gated query, Cloudflare blip) that never resolves AND
 * never rejects, leaving the caller's `await` permanently pending. With
 * `withTimeout(p, ms, fallback)` the worst case becomes "you wait `ms`
 * and then proceed with `fallback`," not "you wait forever."
 *
 * v0.8.11 used an inline `Promise.race` for the workspace query timeout.
 * v0.8.18 extracts the pattern so the AuthProvider's initial
 * `getSession()` (the load-bearing one that gates the blue-screen
 * placeholder) gets the same treatment without the boilerplate.
 *
 * Note: the original promise is NOT cancelled when the timeout wins —
 * JS promises have no cancellation primitive. If the network call ever
 * does resolve, its handlers still run. Callers that need to side-step
 * the late resolution should track an `isCancelled` flag separately.
 */
export async function withTimeout<T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> {
  let timerId: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<T>((resolve) => {
    timerId = setTimeout(() => resolve(fallback), ms)
  })
  try {
    return await Promise.race([promise, timeout])
  } finally {
    if (timerId !== undefined) clearTimeout(timerId)
  }
}
